import { inject, Injectable } from "@angular/core";
import { BehaviorSubject } from "rxjs";
import { AclService } from "src/app/common/services/acl.service";
import { ConfigService } from "src/app/common/services/config.service";

export interface LlmConfigItem {
  backendKey: string;
  technicalName: string;
  displayedName: string;
  description: string;
  gdprCompliant: boolean;
  searchSupported?: boolean;
  imageCreationSupported?: boolean;
}

@Injectable({ providedIn: "root" })
export class LlmConfigService {
  private readonly llmBaseConfigs: Readonly<Record<string, LlmConfigItem>> = {
    GEMINI_1_5_PRO: {
      backendKey: "GEMINI_1_5_PRO",
      technicalName: "gemini-1.5-pro",
      displayedName: $localize`:@@llm_name_gemini_1_5_pro:Google Gemini 1.5 pro (RTS - Google Cloud Zürich)`,
      description: $localize`:@@llm_description_gemini_1_5_pro:\
Modèle Gemini 1.5 Pro de google. Ce modèle peut traiter jusqu'à 500'000 mots à la fois (contexte + requête).`,
      gdprCompliant: true,
      searchSupported: false,
      imageCreationSupported: false,
    },
    GEMINI_1_5_FLASH: {
      backendKey: "GEMINI_1_5_FLASH",
      technicalName: "gemini-1.5-flash",
      displayedName: $localize`:@@llm_name_gemini_1_5_flash:Google Gemini 1.5 flash (RTS - Google Cloud Zürich)`,
      description: $localize`:@@llm_description_gemini_1_5_flash:\
Modèle Gemini 1.5 Flash de google. Ce modèle peut traiter jusqu'à 500'000 mots à la fois (contexte + requête).`,
      gdprCompliant: true,
      searchSupported: false,
      imageCreationSupported: false,
    },
    GEMINI_2_0_FLASH: {
      backendKey: "GEMINI_2_0_FLASH",
      technicalName: "gemini-2.0-flash",
      displayedName: $localize`:@@llm_name_gemini_2_0_flash:Google Gemini 2.0 flash (RTS - Google Cloud Zürich)`,
      description: $localize`:@@llm_description_gemini_2_0_flash:\
Modèle Gemini 2.0 Flash de google. Ce modèle peut traiter jusqu'à 500'000 mots à la fois (contexte + requête).`,
      gdprCompliant: true,
      searchSupported: true,
      imageCreationSupported: false,
    },
    GEMINI_2_0_FLASH_THINKING: {
      backendKey: "GEMINI_2_0_FLASH_THINKING",
      technicalName: "gemini-2.0-flash-thinking-exp",
      displayedName: $localize`:@@llm_name_gemini_2_0_flash_thinking_exp:Google Gemini 2.0 flash thinking exp (RTS - Google Cloud Zürich)`,
      description: $localize`:@@llm_description_gemini_2_0_flash_thinking_exp:\
Modèle Gemini 2.0 Flash Thinking (expérimental) de google. Ce modèle peut traiter jusqu'à 500'000 mots à la fois (contexte + requête).`,
      gdprCompliant: true,
      searchSupported: false,
      imageCreationSupported: false,
    },
    GEMINI_2_5_FLASH_PREVIEW: {
      backendKey: "GEMINI_2_5_FLASH_PREVIEW",
      technicalName: "gemini-2.5-flash-preview-05-20",
      displayedName: $localize`:@@llm_name_gemini_2_5_flash_preview:Google Gemini 2.5 flash preview (RTS - Google Cloud Zürich)`,
      description: $localize`:@@llm_description_gemini_2_5_flash_preview:\
Modèle Gemini 2.5 Flash (preview) de google. Ce modèle peut traiter jusqu'à 500'000 mots à la fois (contexte + requête).`,
      gdprCompliant: true,
      searchSupported: true,
      imageCreationSupported: false,
    },
    GEMINI_2_5_PRO_PREVIEW: {
      backendKey: "GEMINI_2_5_PRO_PREVIEW",
      technicalName: "gemini-2.5-pro-preview-05-06",
      displayedName: $localize`:@@llm_name_gemini_2_5_pro_preview:Google Gemini 2.5 pro preview (RTS - Google Cloud Zürich)`,
      description: $localize`:@@llm_description_gemini_2_5_pro_preview:\
Modèle Gemini 2.5 Pro (preview) de google. Ce modèle peut traiter jusqu'à 500'000 mots à la fois (contexte + requête).`,
      gdprCompliant: true,
      searchSupported: true,
      imageCreationSupported: false,
    },
    GPT_4O: {
      backendKey: "GPT_4O",
      technicalName: "gpt-4o",
      displayedName: $localize`:@@llm_name_gpt_4o:Open AI GPT-4o (US, EUR)`,
      description: $localize`:@@llm_description_gpt_4o:\
Modèle le plus récent et le plus avancé d'openai. Ce modèle peut traiter jusqu'à 65'500 mots à la fois (contexte + requête). \
Idéal pour les tâches qui nécessitent une créativité et un raisonnement exceptionnels.`,
      gdprCompliant: false,
      searchSupported: true,
      imageCreationSupported: false,
    },
    GPT_4O_MINI: {
      backendKey: "GPT_4O_MINI",
      technicalName: "gpt-4o-mini",
      displayedName: $localize`:@@llm_name_gpt_4o_mini:Open AI GPT-4o mini (US, EUR)`,
      description: $localize`:@@llm_description_gpt_4o_mini:\
Modèle le plus rapide et le moins cher d'openai. Idéal pour la plupart des tâches quotidiennes. \
Ce modèle peut traiter jusqu'à 65'000 mots à la fois (contexte + requête).`,
      gdprCompliant: false,
      searchSupported: true,
      imageCreationSupported: true,
    },
    CLAUDE_3_5_SONNET: {
      backendKey: "CLAUDE_3_5_SONNET",
      technicalName: "claude-3-5-sonnet-20240620",
      displayedName: $localize`:@@llm_name_claude_3_5_sonnet:Anthropic Claude 3.5 Sonnet (RTS - AWS - en test)`,
      description: $localize`:@@llm_description_claude_3_5_sonnet:Modèle Claude 3.5 Sonnet d'anthropic.`,
      gdprCompliant: true,
      searchSupported: false,
      imageCreationSupported: false,
    },
    AZURE_OPEN_AI_GPT_4: {
      backendKey: "AZURE_OPEN_AI_GPT_4",
      technicalName: "azure-gpt-4", // this name is tighly coupled with the deployment name in Azure Studio
      displayedName: $localize`:@@llm_name_azure_open_ai_gpt_4:Azure Open AI - GPT-4o (RTS - Azure CH North)`,
      description: $localize`:@@llm_description_azure_open_ai_gpt_4:\
Même modèle que gpt-4 mais hébergé chez Azure (les données ne sont pas envoyées à OpenAI).`,
      gdprCompliant: true,
      searchSupported: false,
      imageCreationSupported: false,
    },
    RSI_LLAMA_3: {
      backendKey: "RSI_LLAMA_3",
      technicalName: "rsi-llama-3",
      displayedName: $localize`:@@llm_name_rsi_llama_3:Meta Llama3 - Safe contexte (RSI - AWS)`,
      description: $localize`:@@llm_description_rsi_llama_3:\
Modèle open source Meta Llama 3 pouvant traiter jusqu'a 4'000 mots à la fois et hébergé à la RSI \
(les données ne sont pas envoyées à Meta.)`,
      gdprCompliant: true,
      searchSupported: false,
      imageCreationSupported: false,
    },
  };

  readonly llmConfigs!: Record<string, LlmConfigItem>;

  hasSearchCapableModelSubject = new BehaviorSubject<boolean>(false);
  hasImageCapableModelSubject = new BehaviorSubject<boolean>(false);

  private readonly configService = inject(ConfigService);
  private readonly aclService = inject(AclService);

  constructor() {
    const availableConfigs = Object.fromEntries(
      Object.entries(this.llmBaseConfigs).filter(([key]) => Object.keys(this.configService.llmConfigs).includes(key)),
    );

    const missingKeys = Object.keys(this.configService.llmConfigs).filter(
      (key) => !Object.keys(this.llmBaseConfigs).includes(key),
    );

    // Get missing configurations present in the backend and add them with default values.
    const missingConfigs: Record<string, LlmConfigItem> = Object.fromEntries(
      missingKeys.map((key) => [
        key,
        {
          backendKey: key,
          technicalName: this.configService.llmConfigs[key]
            ? this.configService.llmConfigs[key].technicalName
            : key.toLowerCase(),
          displayedName: key.toLowerCase(),
          description: $localize`:@@llm_no_description_available_message:\
Aucune description disponible pour ${key.toLowerCase()}`,
          gdprCompliant: false,
        },
      ]),
    );

    this.llmConfigs = {
      ...availableConfigs,
      ...missingConfigs,
    };

    this.parseModelCapabilities();
  }

  parseModelCapabilities(): void {
    this.aclService
      .isAuthorized("USE_NON_GDPR_LLM")
      .then((canUseNonGdpr) => {
        const availableModels = Object.values(this.llmConfigs).filter((m) => m.gdprCompliant || canUseNonGdpr);
        this.hasSearchCapableModelSubject.next(availableModels.some((m) => m.searchSupported));
        this.hasImageCapableModelSubject.next(availableModels.some((m) => m.imageCreationSupported));
      })
      .catch(() => {
        this.hasSearchCapableModelSubject.next(false);
        this.hasImageCapableModelSubject.next(false);
      });
  }

  getTechnicalNameFromBackendKey(backendKey: string): string {
    return this.llmConfigs[backendKey]?.technicalName ?? backendKey;
  }

  async getLlmOptions(): Promise<object[]> {
    const llmOptions: object[] = [];
    llmOptions.push({
      label: $localize`:@@prompt_editor_model_dropdown_group_compliant:Conforme au RGPD`,
      items: Object.values(this.llmConfigs).filter((llm) => llm.gdprCompliant),
    });

    if (await this.aclService.isAuthorized("USE_NON_GDPR_LLM")) {
      llmOptions.push({
        label: $localize`:@@prompt_editor_model_dropdown_group_not_compliant:Non conforme au RGPD`,
        items: Object.values(this.llmConfigs).filter((llm) => !llm.gdprCompliant),
      });
    }
    return llmOptions;
  }

  async isModelSearchEnabled(techName: string): Promise<boolean> {
    const item = Object.values(this.llmConfigs).find((m) => m.technicalName === techName);
    if (!item?.searchSupported) {
      return false;
    }
    const canUseNonGdpr = await this.aclService.isAuthorized("USE_NON_GDPR_LLM");
    return item.gdprCompliant || canUseNonGdpr;
  }
}
